import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { notificationSound } from "@/lib/notification-sound";
import { toast } from "sonner";
import { safeRemoveChannel } from "@/lib/supabase-channel";

export interface PublicOrder {
  id: string;
  order_number: number;
  order_type: string;
  customer_name: string | null;
  customer_phone: string | null;
  delivery_address: string | null;
  table_number: string | null;
  subtotal: number;
  delivery_fee: number;
  total: number;
  status: string;
  created_at: string;
  is_public_order: boolean;
}

export function usePublicOrderNotifications() {
  const { activeRestaurant } = useRestaurantContext();
  const [newPublicOrders, setNewPublicOrders] = useState<PublicOrder[]>([]);
  const [hasNewOrders, setHasNewOrders] = useState(false);

  const clearNewOrders = useCallback(() => {
    setNewPublicOrders([]);
    setHasNewOrders(false);
  }, []);

  const dismissOrder = useCallback((orderId: string) => {
    setNewPublicOrders((prev) => prev.filter((o) => o.id !== orderId));
    setNewPublicOrders((current) => {
      if (current.length === 0) {
        setHasNewOrders(false);
      }
      return current;
    });
  }, []);

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!activeRestaurant) return;

    console.log("[PublicOrderNotifications] Setting up subscription for restaurant:", activeRestaurant.id);

    // Clean up previous channel if exists
    if (channelRef.current) {
      safeRemoveChannel(channelRef.current);
      channelRef.current = null;
    }

    channelRef.current = supabase
      .channel(`public-orders-${activeRestaurant.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "orders",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        async (payload) => {
          console.log("[PublicOrderNotifications] New order received:", payload);
          
          const newOrderPayload = payload.new as PublicOrder;
          
          // Only notify for public orders (from digital menu or QR code)
          if (newOrderPayload.is_public_order) {
            console.log("[PublicOrderNotifications] Public order detected, triggering notification");
            
            // Play alert sound
            await notificationSound.playNewOrder();
            
            // Fetch the complete order data with updated totals
            // (the realtime payload may not have the total calculated yet)
            const { data: completeOrder } = await supabase
              .from("orders")
              .select("id, order_number, order_type, customer_name, customer_phone, delivery_address, table_number, subtotal, delivery_fee, total, status, created_at, is_public_order")
              .eq("id", newOrderPayload.id)
              .maybeSingle();
            
            const orderToAdd = completeOrder || newOrderPayload;
            
            // Add to new orders list
            setNewPublicOrders((prev) => [orderToAdd as PublicOrder, ...prev]);
            setHasNewOrders(true);
            
            // Show toast notification
            const orderType = orderToAdd.table_number 
              ? `Mesa ${orderToAdd.table_number}` 
              : orderToAdd.order_type === "tab" 
                ? "Delivery" 
                : "Retirada";
            
            toast.info(`🔔 Novo Pedido #${orderToAdd.order_number}`, {
              description: `${orderType}${orderToAdd.customer_name ? ` - ${orderToAdd.customer_name}` : ""}`,
              duration: 10000,
              action: {
                label: "Ver",
                onClick: () => {
                  window.location.href = "/dashboard/pedidos";
                },
              },
            });
          }
        }
      )
      .subscribe((status) => {
        console.log("[PublicOrderNotifications] Subscription status:", status);
      });

    return () => {
      console.log("[PublicOrderNotifications] Cleaning up subscription");
      safeRemoveChannel(channelRef.current);
      channelRef.current = null;
    };
  }, [activeRestaurant]);

  return {
    newPublicOrders,
    hasNewOrders,
    clearNewOrders,
    dismissOrder,
  };
}
